import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Polls /api/reporting/marketplace/pending-jobs every 15s.
 * Shows a pulsing badge with count when jobs are running.
 * Shows a toast when jobs complete (count decreases).
 */
const PendingJobsIndicator = ({ token, onClick }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const prevJobsRef = useRef([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/reporting/marketplace/pending-jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const newJobs = res.data?.jobs || [];
        const newIds = new Set(newJobs.map((j) => j.upload_id));
        const prevJobs = prevJobsRef.current;

        // Detect completions: jobs that were in prev but not in current
        const completedJobs = prevJobs.filter((j) => !newIds.has(j.upload_id));
        for (const j of completedJobs) {
          toast.success(`${j.ssp_source} fetch complete`, {
            description: `${j.summary?.date_range?.start} → ${j.summary?.date_range?.end}`,
          });
        }

        prevJobsRef.current = newJobs;
        setJobs(newJobs);
      } catch {
        // silent fail — indicator will just not update
      }
    };

    poll();
    const interval = setInterval(poll, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  if (jobs.length === 0) return null;

  return (
    <div className="relative" data-testid="pending-jobs-indicator">
      <button
        onClick={() => {
          setExpanded((v) => !v);
          if (onClick) onClick();
        }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#3b82f6]/10 border border-[#3b82f6]/30 hover:bg-[#3b82f6]/15 transition-colors"
        data-testid="pending-jobs-btn"
      >
        <div className="relative">
          <Activity className="w-3.5 h-3.5 text-[#3b82f6]" />
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#3b82f6] animate-pulse" />
        </div>
        <span className="text-[#3b82f6] font-mono text-xs flex-1 text-left">
          {jobs.length} fetch{jobs.length > 1 ? "es" : ""} running
        </span>
        <Loader2 className="w-3 h-3 text-[#3b82f6] animate-spin" />
      </button>
      {expanded && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#1a1a1a] border border-white/10 rounded-lg p-2 shadow-xl z-50" data-testid="pending-jobs-dropdown">
          {jobs.map((j) => (
            <div key={j.upload_id} className="px-2 py-1.5 text-[11px] font-mono">
              <div className="flex items-center justify-between">
                <span className="text-[#3b82f6]">{j.ssp_source}</span>
                <span className="text-[#ebebeb]/40">
                  {j.summary?.date_range?.start} → {j.summary?.date_range?.end}
                </span>
              </div>
              <div className="text-[#ebebeb]/40 text-[10px] mt-0.5">
                Queued {new Date(j.uploaded_at).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingJobsIndicator;
